import { FC } from 'react';

import { Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import { FieldInput } from 'src/components';
import CustomCountrySelect from 'src/components/CustomCountrySelect/CustomCountrySelect';
import { PhoneNumberField } from 'src/components/FormikComponents';
import Loader from 'src/components/Loader/Loader';
import { SearchInput } from 'src/components/SearchInput';
import { useValidateEmail } from 'src/hooks';

import { Card } from '../card';

interface ContactInformationComponent {
    heading: string;
    formik: FormikProps<unknown>;
    namePrefix?: string;
    onFindAddress: (value: string, formValue: string) => Promise<void>;
    postalLabel: string;
    formattedAddress?: string;
}

const ContactInformationComponent: FC<ContactInformationComponent> = ({
    heading,
    formik,
    namePrefix,
    onFindAddress,
    postalLabel,
    formattedAddress,
}) => {
    const { onValidateEmail, validatingEmail } = useValidateEmail();

    return (
        <Card heading={heading}>
            {validatingEmail && <Loader />}
            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name={`${namePrefix}firstName`}
                    label="First Name"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name={`${namePrefix}lastName`} label="Last Name" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <SearchInput
                    name={`${namePrefix}email`}
                    label="Email Address"
                    value={formik.values[`${namePrefix}email`]}
                    onChange={(e) => {
                        formik.handleChange(e);
                    }}
                    error={formik.errors.email}
                    boxStyling={{ maxWidth: '50%' }}
                    searchText="Validate"
                    onSearch={async (email) => {
                        if (email) {
                            const trimmedEmail = email.trim();

                            await onValidateEmail(
                                trimmedEmail,
                                () => {
                                    formik.setFieldError(
                                        `${namePrefix}email`,
                                        'Please enter a valid email'
                                    );
                                },
                                () => {
                                    formik.setFieldError(`${namePrefix}email`, null);
                                }
                            );
                        }
                    }}
                />

                <PhoneNumberField
                    formikFieldName={`${namePrefix}telephoneNumber`}
                    label="Telephone Number"
                    placeholder="Enter Telephone Number"
                />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <PhoneNumberField
                    formikFieldName={`${namePrefix}mobilePhoneNumber`}
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                />
                <FieldInput name="bci_position" label="Position" boxStyling={{ maxWidth: '50%' }} />
            </Box>
            <Typography sx={{ fontSize: '20px', color: '#434341' }}>Registered Address</Typography>
            <Box height="2px" width="100%" margin="0px auto 24px" bgcolor="#F39E4E" />

            {/* TODO: (KM) Fix this to use search */}
            <SearchInput
                name={`${namePrefix}ra_eircode`}
                label={postalLabel}
                value={formik.values[`${namePrefix}ra_eircode`]}
                onChange={(e) => {
                    formik.handleChange(e);
                }}
                error={formik.errors[`${namePrefix}ra_eircode`]}
                boxStyling={{ maxWidth: '50%' }}
                onSearch={(value) => {
                    return onFindAddress(value, `${namePrefix}ra`);
                }}
            />
            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name={`${namePrefix}ra_addressLine1`}
                    label="Address Line 1"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name={`${namePrefix}ra_addressLine2`} label="Address Line 2" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <FieldInput
                    name={`${namePrefix}ra_city`}
                    label="Town/City"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <CustomCountrySelect
                    label="Country"
                    field={`${namePrefix}ra_country`}
                    setFieldValue={formik.setFieldValue}
                    selectedCountry={formik.values[`${namePrefix}ra_country`]}
                    setSelectedCountry={(country) => {
                        return formik.setFieldValue(`${namePrefix}ra_country`, country);
                    }}
                    error={formik.errors[`${namePrefix}ra_country`]}
                    formValue={formik.values[`${namePrefix}ra_country`]}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <FieldInput
                    name={`${namePrefix}ra_eircode`}
                    label={postalLabel}
                    boxStyling={{ maxWidth: '50%' }}
                />
            </Box>
        </Card>
    );
};

export default ContactInformationComponent;
